import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, ExternalLink, Package } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { QuickEditSideSheet, type QuickEditValue } from '~/components/product/quick-edit-side-sheet';
import { GridChrome, type ChipCounts, type Density } from './grid-chrome';
import { GridTable, type RowAction } from './grid-table';
import { FiltersSheet } from './filters-sheet';
import { MassEditDialog, type MassEditValue } from './mass-edit-dialog';
import { useProductsList, type ProductChip } from '~/hooks/use-products-list';
import { useProductCounts } from '~/hooks/use-product-counts';
import { useProductMutations } from '~/hooks/use-product-mutations';
import { useColumnVisibility, PRODUCT_SORT_SELECTORS } from '~/hooks/use-column-visibility';
import { cycleSort, sortFromParams, writeSortParams } from '~/lib/grid/sort';
import {
	filtersFromParams,
	filtersToParam,
	activeFilterCount as countFilters,
	EMPTY_FILTERS,
	FILTER_PARAM
} from '~/lib/products/product-filters';
import type { ProductGridRow } from '~/lib/fixtures/demo-products';

const CHIPS: ProductChip[] = ['outOfStock', 'hidden', 'newReleases', 'featured', 'noImage'];

/** Availability options for the quick-edit sheet — value is the AvailabilityStatus enum id. */
const AVAILABILITY_VALUES = [0, 1, 2, 3, 4, 5] as const;

function defaultQuickEdit(row: ProductGridRow): QuickEditValue {
	return {
		storefrontVisible: row.isVisible,
		availability: String(row.availabilityStatus ?? 1),
		showSocial: false,
		featured: row.isFeatured,
		used: false,
		stateNewRelease: false,
		stateOnSale: false,
		stateLimited: false,
		stateBestseller: false
	};
}

function EmptyState({ filtered, onClear }: { filtered: boolean; onClear: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col items-center gap-3 rounded-b-md border border-t-0 border-border bg-surface py-16 text-center">
			<Package size={40} className="text-foreground-disabled" strokeWidth={1.25} />
			<h2 className="text-md font-semibold text-foreground">{t(filtered ? 'products.empty.filteredTitle' : 'products.empty.title')}</h2>
			<p className="text-sm text-foreground-muted">{t(filtered ? 'products.empty.filteredSubtitle' : 'products.empty.subtitle')}</p>
			{filtered && (
				<Button variant="outline" onClick={onClear}>
					{t('products.empty.clearFilters')}
				</Button>
			)}
		</div>
	);
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col items-center gap-3 rounded-b-md border border-t-0 border-border bg-surface py-16 text-center">
			<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
			<h2 className="text-md font-semibold text-foreground">{t('products.error.title')}</h2>
			<p className="text-sm text-foreground-muted">{t('products.error.subtitle')}</p>
			<Button variant="outline" onClick={onRetry}>
				{t('common.retry')}
			</Button>
		</div>
	);
}

/** Products grid wired to the live API (useProductsList + useProductCounts + mutations). */
export function ProductsGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	// URL-driven state
	const pageIndex = Math.max(0, Number(searchParams.get('page') ?? '1') - 1);
	const pageSize = [25, 50, 100].includes(Number(searchParams.get('pageSize'))) ? Number(searchParams.get('pageSize')) : 25;
	const search = searchParams.get('q') ?? '';
	const chipParam = searchParams.get('chip');
	const activeChip: ProductChip | null = CHIPS.includes(chipParam as ProductChip) ? (chipParam as ProductChip) : null;

	const updateParams = (mutate: (next: URLSearchParams) => void) => {
		const next = new URLSearchParams(searchParams);
		mutate(next);
		setSearchParams(next, { replace: true });
	};

	// Debounced search → loadOptions.searchValue
	const [searchInput, setSearchInput] = useState(search);
	useEffect(() => setSearchInput(search), [search]);
	useEffect(() => {
		if (searchInput === search) return;
		const id = setTimeout(() => {
			updateParams(p => {
				if (searchInput.trim()) p.set('q', searchInput.trim());
				else p.delete('q');
				p.delete('page');
			});
		}, 350);
		return () => clearTimeout(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchInput]);

	// Advanced filters (persisted in the `f` searchParam as compact JSON).
	const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
	const filtersActive = countFilters(filters);
	const [filtersOpen, setFiltersOpen] = useState(false);

	const applyFiltersToUrl = (next: typeof filters) =>
		updateParams(p => {
			const param = filtersToParam(next);
			if (param) p.set(FILTER_PARAM, param);
			else p.delete(FILTER_PARAM);
			p.delete('page'); // any filter change resets to page 1
		});

	// Sort (URL-driven). The grid column key maps to a backend projection selector.
	const sort = useMemo(() => sortFromParams(searchParams), [searchParams]);
	const sortSelector = sort ? (PRODUCT_SORT_SELECTORS[sort.key] ?? null) : null;
	const onSort = (key: string) =>
		updateParams(p => {
			writeSortParams(p, cycleSort(sort, key));
			p.delete('page'); // a sort change resets to page 1
		});

	const skip = pageIndex * pageSize;
	const { data, isLoading, isError, refetch } = useProductsList({
		skip,
		take: pageSize,
		search,
		chip: activeChip,
		filters,
		sortSelector,
		sortDir: sort?.dir
	});
	const { data: counts } = useProductCounts();
	const { remove, duplicate, quickEdit, bulkEdit, erpSync } = useProductMutations();

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;

	const chipCounts: ChipCounts = {
		outOfStock: counts?.outOfStock,
		hidden: counts?.hidden,
		newReleases: counts?.newReleases,
		featured: counts?.featured,
		noImage: counts?.noImage
	};

	// Selection (per current page; cleared on page/filter change)
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const filtersParam = searchParams.get(FILTER_PARAM);
	useEffect(() => setSelectedIds(new Set()), [pageIndex, pageSize, search, activeChip, filtersParam]);

	// Column visibility (localStorage-persisted; frontend-only).
	const { visible: visibleColumns, toggle: toggleColumn, reset: resetColumns } = useColumnVisibility();

	const toggleRow = (id: number, checked: boolean) =>
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (checked) next.add(id);
			else next.delete(id);
			return next;
		});
	const toggleAll = (checked: boolean) => setSelectedIds(checked ? new Set(rows.map(r => r.id)) : new Set());

	// Density (URL-persisted so it survives reload)
	const density: Density = searchParams.get('density') === 'compact' ? 'compact' : 'comfortable';
	const setDensity = (d: Density) => updateParams(p => (d === 'compact' ? p.set('density', 'compact') : p.delete('density')));

	const productTypeLabel = (type: number | null) => (type === 1 ? t('products.type.toy') : t('products.type.book'));

	// Quick edit
	const [quickEditRow, setQuickEditRow] = useState<ProductGridRow | null>(null);
	const [quickEditValue, setQuickEditValue] = useState<QuickEditValue | null>(null);
	const unsavedCount = useMemo(() => {
		if (!quickEditRow || !quickEditValue) return 0;
		const base = defaultQuickEdit(quickEditRow);
		return (Object.keys(base) as (keyof QuickEditValue)[]).reduce((n, k) => (base[k] !== quickEditValue[k] ? n + 1 : n), 0);
	}, [quickEditRow, quickEditValue]);

	const openQuickEdit = (row: ProductGridRow) => {
		setQuickEditRow(row);
		setQuickEditValue(defaultQuickEdit(row));
	};
	const closeQuickEdit = () => {
		setQuickEditRow(null);
		setQuickEditValue(null);
	};
	const saveQuickEdit = () => {
		if (!quickEditRow || !quickEditValue) return;
		// Wired: storefront visibility + availability + featured/used/social flags via bulkEditPost.
		// TODO: storefront state toggles (newRelease/onSale/limited/bestseller) have no bulkEditPost
		//       field — they need a dedicated product-states endpoint (flag to backend).
		quickEdit.mutate(
			{
				productIds: [quickEditRow.id],
				visible: quickEditValue.storefrontVisible,
				featured: quickEditValue.featured,
				used: quickEditValue.used,
				social: quickEditValue.showSocial,
				availability: Number(quickEditValue.availability)
			},
			{
				onSuccess: () => {
					toast.success(t('products.toasts.quickEditSaved'));
					closeQuickEdit();
				},
				onError: () => toast.error(t('products.toasts.actionFailed'))
			}
		);
	};

	// Delete confirm
	const [deleteRow, setDeleteRow] = useState<ProductGridRow | null>(null);
	const confirmDelete = () => {
		if (!deleteRow) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('products.toasts.deleted'));
				setDeleteRow(null);
			},
			onError: () => toast.error(t('products.toasts.actionFailed'))
		});
	};

	const onRowAction = (row: ProductGridRow, action: RowAction) => {
		switch (action) {
			case 'open':
				navigate(`/products/${row.id}`);
				break;
			case 'quickEdit':
				openQuickEdit(row);
				break;
			case 'delete':
				setDeleteRow(row);
				break;
			case 'duplicate':
				duplicate.mutate(row.id, {
					onSuccess: created => {
						toast.success(t('products.toasts.duplicated'));
						if (created?.id) navigate(`/products/${created.id}`);
					},
					onError: () => toast.error(t('products.toasts.actionFailed'))
				});
				break;
			default:
				// warehouses / related / sync — not yet migrated.
				toast.info(t('products.toasts.comingSoon'));
		}
	};

	const onChipToggle = (chip: ProductChip) =>
		updateParams(p => {
			if (activeChip === chip) p.delete('chip');
			else p.set('chip', chip);
			p.delete('page');
		});

	const onStub = () => toast.info(t('products.toasts.comingSoon'));

	// Filters badge: chip counts as one active group on top of the advanced filters.
	const activeFilterCount = filtersActive + (activeChip ? 1 : 0);

	// Mass edit (bulkEditPost over the current selection).
	const [massEditOpen, setMassEditOpen] = useState(false);
	const applyMassEdit = (value: MassEditValue) => {
		const ids = [...selectedIds];
		if (ids.length === 0) return;
		bulkEdit.mutate(
			{
				productIds: ids,
				...(value.visible !== null && { visible: value.visible }),
				...(value.featured !== null && { featured: value.featured }),
				...(value.used !== null && { used: value.used }),
				...(value.social !== null && { social: value.social }),
				...(value.availability !== null && { availability: value.availability })
			},
			{
				onSuccess: () => {
					toast.success(t('products.massEdit.success', { count: ids.length }));
					setMassEditOpen(false);
					setSelectedIds(new Set());
				},
				onError: () => toast.error(t('products.toasts.actionFailed'))
			}
		);
	};

	// Mass ERP sync (forceERPSync over the selected product ids).
	const massErpSync = () => {
		const ids = [...selectedIds];
		if (ids.length === 0) return;
		erpSync.mutate(ids, {
			onSuccess: () => {
				toast.success(t('products.toasts.erpSyncDone'));
				refetch();
			},
			onError: () => toast.error(t('products.toasts.actionFailed'))
		});
	};

	return (
		<div className="flex flex-col gap-3">
			<GridChrome
				search={searchInput}
				onSearch={setSearchInput}
				activeChip={activeChip}
				onChipToggle={onChipToggle}
				counts={chipCounts}
				activeFilterCount={activeFilterCount}
				onOpenFilters={() => setFiltersOpen(true)}
				onNew={() => navigate('/products/create')}
				onExport={onStub}
				onMassEdit={() => setMassEditOpen(true)}
				onMassErpSync={massErpSync}
				massErpSyncPending={erpSync.isPending}
				visibleColumns={visibleColumns}
				onToggleColumn={toggleColumn}
				onResetColumns={resetColumns}
				selectedCount={selectedIds.size}
				pageIndex={pageIndex}
				onClearSelection={() => setSelectedIds(new Set())}
				rangeFrom={total === 0 ? 0 : skip + 1}
				rangeTo={Math.min(skip + pageSize, total)}
				total={total}
				density={density}
				onDensityChange={setDensity}
				onRefresh={() => refetch()}
			/>

			{isError ? (
				<ErrorState onRetry={() => refetch()} />
			) : !isLoading && rows.length === 0 ? (
				<EmptyState
					filtered={Boolean(search || activeChip || filtersActive)}
					onClear={() =>
						updateParams(p => {
							p.delete('q');
							p.delete('chip');
							p.delete(FILTER_PARAM);
							p.delete('page');
						})
					}
				/>
			) : (
				<GridTable
					rows={rows}
					loading={isLoading}
					density={density}
					visibleColumns={visibleColumns}
					productTypeLabel={productTypeLabel}
					selectedIds={selectedIds}
					onToggleRow={toggleRow}
					onToggleAll={toggleAll}
					onRowAction={onRowAction}
					sort={sort}
					onSort={onSort}
					pageIndex={pageIndex}
					pageSize={pageSize}
					total={total}
					onPageChange={p => updateParams(next => next.set('page', String(p + 1)))}
					onPageSizeChange={size =>
						updateParams(next => {
							next.set('pageSize', String(size));
							next.delete('page');
						})
					}
				/>
			)}

			{/* Quick edit */}
			{quickEditRow && quickEditValue && (
				<QuickEditSideSheet
					open
					onOpenChange={open => {
						if (!open) closeQuickEdit();
					}}
					product={{
						name: quickEditRow.name,
						sku: quickEditRow.sku,
						barcode: quickEditRow.barcode ?? undefined,
						thumbUrl: quickEditRow.imageUrl ?? undefined
					}}
					value={quickEditValue}
					onChange={setQuickEditValue}
					availabilityOptions={AVAILABILITY_VALUES.map(v => ({ value: String(v), label: t(`products.availability.${v}`) }))}
					unsavedCount={unsavedCount}
					onSave={saveQuickEdit}
					onClose={closeQuickEdit}
					openFullProductSlot={
						<button
							type="button"
							onClick={() => navigate(`/products/${quickEditRow.id}`)}
							className="inline-flex items-center gap-1 text-xs font-medium text-brand-subtle-fg hover:underline">
							{t('products.quickEdit.openFull')}
							<ExternalLink size={11} />
						</button>
					}
				/>
			)}

			{/* Delete confirm */}
			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('products.deleteDialog.title')}</DialogTitle>
						<DialogDescription>{t('products.deleteDialog.body', { name: deleteRow?.name ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('products.deleteDialog.confirm')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Advanced filters */}
			<FiltersSheet
				open={filtersOpen}
				onOpenChange={setFiltersOpen}
				value={filters}
				onApply={next => {
					applyFiltersToUrl(next);
					setFiltersOpen(false);
				}}
				onClear={() => {
					applyFiltersToUrl(EMPTY_FILTERS);
					setFiltersOpen(false);
				}}
			/>

			{/* Mass edit */}
			<MassEditDialog
				open={massEditOpen}
				onOpenChange={setMassEditOpen}
				count={selectedIds.size}
				availabilityOptions={AVAILABILITY_VALUES.map(v => ({ value: String(v), label: t(`products.availability.${v}`) }))}
				pending={bulkEdit.isPending}
				onApply={applyMassEdit}
			/>
		</div>
	);
}
